import React from 'react';
import { Link } from 'react-router-dom';
import { SmartphoneIcon, CloudUploadIcon, ImageIcon, ArrowLeftIcon, SparklesIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LogoIcon } from '../components/LogoIcon';
import { PhotoTransformerPlugin } from '../components/InteriorAI/PhotoTransformerPlugin';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function InteriorAIPage() {
  const revealRef = useScrollReveal();
  return <div className="min-h-screen bg-white text-apple-black font-sans selection:bg-apple-blue selection:text-white relative overflow-hidden" data-id="element-582" data-mp="24vztjmjdjlw9">
      {/* Decorative background orbs */}
      <div className="gradient-orb w-[600px] h-[600px] bg-blue-300 top-[-100px] -right-[200px] fixed" data-id="element-583" data-mp="1vus9o9hlunlp" />
      <div className="gradient-orb w-[500px] h-[500px] bg-purple-300 top-[600px] -left-[200px] fixed" data-id="element-584" data-mp="fgtnmwfia3u0" />

      {/* Simplified Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100/80" data-id="element-585" data-mp="kebodzjddcqx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-586" data-mp="m8gsdirivqly">
          <div className="flex justify-between items-center h-16" data-id="element-587" data-mp="ma0cxlk7ontj">
            <Link to="/" className="flex items-center gap-2 text-apple-gray hover:text-apple-black transition-colors" data-id="element-588" data-mp="108zvqcl1m8hs">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-589" data-mp="198mxx0lljibw" />
              <span className="font-medium text-sm" data-id="element-590" data-mp="1973jzgnexjda">
                Späť na hlavnú stránku
              </span>
            </Link>
            <div className="flex items-center gap-3" data-id="element-591" data-mp="1558jgedb3a0k">
              <LogoIcon size={32} data-id="element-592" data-mp="1ixer1c2e88oe" />
              <span className="text-lg font-bold tracking-tight text-apple-black hidden sm:inline" data-id="element-593" data-mp="1j0kbuf3zooeb">
                nexify-studio.tech
              </span>
            </div>
            <div className="w-[150px] flex justify-end" data-id="element-594" data-mp="1sx5jz9bb2fmd">
              <Button size="small" className="!bg-apple-black hover:!bg-gray-800 !text-white !rounded-full !px-4 !border-none" data-id="element-595" data-mp="1nxsushwyowgm">
                Prihlásiť sa
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-16" ref={revealRef} data-id="element-596" data-mp="24yx5c593ptmv">
        {/* Hero Section */}
        <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-597" data-mp="24x3yaoukt7ec">
          <div className="reveal mb-12" data-id="element-598" data-mp="29rgix27sgoro">
            <h1 className="text-[48px] font-bold tracking-tight text-apple-black mb-6 leading-tight" data-id="element-599" data-mp="2ephyhd60a5ye">
              Interior AI
            </h1>
            <h2 className="text-[30px] font-medium text-apple-gray mb-6" data-id="element-600" data-mp="2iseo3ntyr2bu">
              Staň sa dizajnérom svojho priestoru
            </h2>
            <p className="text-[21px] text-apple-gray font-normal max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-601" data-mp="2sp15g2n2zb94">
              Prinášame modernú AI aplikáciu, cez ktorú si vytvoríš vlastný
              dizajn interiéru svojho domu, bytu či iného priestoru.
            </p>
            <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold text-lg btn-hover-premium !border-none shadow-[0_0_30px_rgba(0,113,227,0.3)]" data-id="element-602" data-mp="ycbbxm0mk429">
              Chcem Pro verziu
            </Button>
          </div>

          <div className="reveal reveal-delay-1 max-w-5xl mx-auto" data-id="element-603" data-mp="c2b80cz56s6e">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-gray-50/50 backdrop-blur-sm" data-id="element-604" data-mp="bzynbs2023pn">
              <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&h=600&fit=crop&crop=center" alt="Moderný interiér navrhnutý AI" className="w-full h-[400px] md:h-[600px] object-cover" data-id="element-605" data-mp="14r06s5cq6lez" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-606" data-mp="uux1iqmku6nt" />
              <div className="absolute bottom-8 left-8 flex gap-3" data-id="element-607" data-mp="3kfi9k7496lv7">
                <span className="bg-white/90 backdrop-blur-md text-apple-black text-sm font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2" data-id="element-608" data-mp="3fh8dluapfh8j">
                  <SparklesIcon className="w-4 h-4 text-apple-blue" data-id="element-609" data-mp="1cy1me7j9vqp7" />
                  Vygenerované za 3.2s
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Ako to funguje */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50/50" data-id="element-610" data-mp="1399we4xrvggx">
          <div className="max-w-7xl mx-auto" data-id="element-611" data-mp="1qze00cayng7x">
            <div className="reveal text-center mb-16" data-id="element-612" data-mp="1vx5pz0s1os9h">
              <h2 className="text-[36px] font-bold tracking-tight text-apple-black" data-id="element-613" data-mp="1vvupb9twc0ml">
                Ako Interior AI funguje
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-614" data-mp="1lygim1xryq7h">
              {/* Connecting line for desktop */}
              <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-blue-100 via-purple-100 to-blue-100 z-0" data-id="element-615" data-mp="5jos5uk6vt0t" />

              {/* Step 1 */}
              <div className="reveal reveal-delay-1 relative z-10 flex flex-col items-center text-center" data-id="element-616" data-mp="2oqso5g2eoyyk">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-617" data-mp="2jrfvufsrmzol">
                  <SmartphoneIcon className="w-10 h-10 text-apple-blue" data-id="element-618" data-mp="2xq60cghx01mz" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-619" data-mp="2tokh8deser4c">
                  Odfoť svoj priestor
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-620" data-mp="2129fgzfg2k5y">
                  Vezmi svoj smartfón a urob fotku miestnosti, ktorú chceš
                  prerobiť. Nezáleží na tom, či je prázdna alebo plná starého
                  nábytku.
                </p>
              </div>

              {/* Step 2 */}
              <div className="reveal reveal-delay-2 relative z-10 flex flex-col items-center text-center" data-id="element-621" data-mp="1mbsibs9uegev">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-622" data-mp="1ib07wvp8i5h0">
                  <CloudUploadIcon className="w-10 h-10 text-purple-500" data-id="element-623" data-mp="3mfcwhgu4xspg" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-624" data-mp="3md08alxsam3y">
                  Nahraj do aplikácie
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-625" data-mp="1o2dr7onut">
                  Nahraj fotku do nášho zabezpečeného cloudu. Vyber si typ
                  miestnosti, preferovaný štýl a farebnú paletu.
                </p>
              </div>

              {/* Step 3 */}
              <div className="reveal reveal-delay-3 relative z-10 flex flex-col items-center text-center" data-id="element-626" data-mp="38f2243tm9vzj">
                <div className="w-24 h-24 bg-white rounded-full shadow-xl flex items-center justify-center mb-8 border border-gray-100" data-id="element-627" data-mp="3dey6i4687pfm">
                  <ImageIcon className="w-10 h-10 text-emerald-500" data-id="element-628" data-mp="np31kfewip6y" />
                </div>
                <h3 className="text-[24px] font-bold text-apple-black mb-4" data-id="element-629" data-mp="smw6z68drf66">
                  Vygeneruj fotografiu
                </h3>
                <p className="text-apple-gray font-light leading-relaxed max-w-xs" data-id="element-630" data-mp="287etxsm76wxv">
                  Umelá inteligencia analyzuje priestor a za pár sekúnd ti
                  ponúkne fotorealistické návrhy tvojho nového vysnívaného
                  interiéru.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Live AI Plugin Section */}
        <section id="nahrat-fotku" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-631" data-mp="1tg62ddyqj1i3">
          <PhotoTransformerPlugin data-id="element-632" data-mp="1pj9w11s8266z" />
        </section>

        {/* CTA Form */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-center" data-id="element-633" data-mp="1ki2nv1m634zs">
          <div className="reveal" data-id="element-634" data-mp="1fkaltert62hv">
            <h2 className="text-3xl md:text-4xl font-bold text-apple-black mb-4" data-id="element-635" data-mp="3evtvjvkxresl">
              Pripravený na zmenu?
            </h2>
            <p className="text-lg text-apple-gray mb-10" data-id="element-636" data-mp="39whb1hhjejt6">
              Zanechaj nám kontakt a získaj prednostný prístup k Pro verzii s
              50% zľavou.
            </p>

            <form className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 text-left space-y-6" data-id="element-637" data-mp="2nq80u7mhkarf">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-638" data-mp="2924vn0g9p33o">
                <Input label="Meno a priezvisko" placeholder="Jozef Mrkvička" data-id="element-639" data-mp="28zqymm211gth" />
                <Input label="E-mail" type="email" placeholder="jozef@priklad.sk" data-id="element-640" data-mp="2dyv71ah4nair" />
              </div>
              <Input label="Telefón (voliteľné)" type="tel" placeholder="+421 900 000 000" data-id="element-641" data-mp="250rp0o5covvp" />
              <Button size="large" className="w-full !bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-xl !py-4 !font-bold" data-id="element-642" data-mp="1v1tzbhw6uwb4">
                Získať prednostný prístup
              </Button>
              <p className="text-xs text-center text-apple-gray mt-4" data-id="element-643" data-mp="3pejue2yambk3">
                Odoslaním súhlasíte so spracovaním osobných údajov.
              </p>
            </form>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-apple-light to-gray-100 pt-16 pb-8 px-4 sm:px-6 lg:px-8 mt-12" data-id="element-644" data-mp="1nib144ediwhy">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent" data-id="element-645" data-mp="1ir2svbos5h6d" />
        <div className="max-w-7xl mx-auto" data-id="element-646" data-mp="1whodb4tvtz6d">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8" data-id="element-647" data-mp="1wku3yoi5t947">
            <div className="flex items-center gap-3" data-id="element-648" data-mp="1sjqm5wgjx30c">
              <LogoIcon size={32} data-id="element-649" data-mp="26g41p01iyuc6" />
              <span className="text-sm font-bold text-apple-black tracking-tight" data-id="element-650" data-mp="26f2r0wof89uz">
                nexify-studio.tech Interior AI
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-apple-gray" data-id="element-651" data-mp="c3xjhha7rdad">
              <Link to="/" className="hover:text-apple-black transition-colors duration-300" data-id="element-652" data-mp="75cpw0pgvruw">
                Hlavná stránka
              </Link>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-653" data-mp="2bzjvcg54dyp4">
                Cenník
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-654" data-mp="230eyde97idf2">
                Galéria
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-655" data-mp="2ur2zwreslkt9">
                Kontakt
              </a>
            </div>
          </div>
          <div className="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-8" data-id="element-656" data-mp="2lzar0l5f2jqs" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-apple-gray" data-id="element-657" data-mp="3ignfd0bqlzxs">
            <p data-id="element-658" data-mp="3dovoeooo5s74">
              Copyright © 2026 nexify-studio.tech. Všetky práva
              vyhradené.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2" data-id="element-659" data-mp="108vnw3u695qa">
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-660" data-mp="156x66mjrx10p">
                Ochrana Súkromia
              </a>
              <a href="#" className="hover:text-apple-black transition-colors duration-300" data-id="element-661" data-mp="1e7vaz721dckc">
                Podmienky Používania
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}